import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft,
  ArrowRight,
  Package,
  Palette,
  Ruler,
  Tag,
  Barcode,
  DollarSign,
  Check,
  Search
} from "lucide-react";

const API = "/api";

const STEPS = [
  { id: 1, title: "Ürün Bilgileri", icon: Package },
  { id: 2, title: "Renk Seçimi", icon: Palette },
  { id: 3, title: "Beden Seçimi", icon: Ruler },
  { id: 4, title: "Özellikler", icon: Tag },
  { id: 5, title: "Barkod", icon: Barcode },
  { id: 6, title: "Fiyatlandırma", icon: DollarSign },
];

export default function UrunAcPage() {
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    urunKodu: "",
    urunAdi: "",
    kdvOrani: "8",
    hierarsi: "",
    renkler: [],
    bedenler: [],
    ozellikler: {},
    barkodlar: [],
    fiyatlar: {
      satinAlma: "",
      toptanSatis: "",
      ilkSatis: "",
      perakendeSatis: ""
    }
  });

  // Seçenekler
  const [hierarsiler, setHierarsiler] = useState([]);
  const [hierarsiArama, setHierarsiArama] = useState("");
  const [renkler, setRenkler] = useState([]);
  const [renkArama, setRenkArama] = useState("");
  const [bedenler, setBedenler] = useState([]);

  useEffect(() => {
    fetchHierarsiler();
    fetchRenkler();
    fetchBedenler();
  }, [token]);

  const fetchHierarsiler = async () => {
    try {
      const response = await axios.get(`${API}/nebim/hierarsiler?token=${token}`);
      if (response.data.success) {
        setHierarsiler(response.data.hierarsiler || []);
      }
    } catch (error) {
      console.error("Hierarşiler alınamadı:", error);
    }
  };

  const fetchRenkler = async () => {
    try {
      const response = await axios.get(`${API}/nebim/renkler?token=${token}`);
      if (response.data.success) {
        setRenkler(response.data.renkler || []);
      }
    } catch (error) {
      console.error("Renkler alınamadı:", error);
    }
  };

  const fetchBedenler = async () => {
    try {
      const response = await axios.get(`${API}/nebim/bedenler?token=${token}`);
      if (response.data.success) {
        setBedenler(response.data.bedenler || []);
      }
    } catch (error) {
      console.error("Bedenler alınamadı:", error);
    }
  };

  const handleRenkToggle = (renk) => {
    setFormData(prev => ({
      ...prev,
      renkler: prev.renkler.includes(renk)
        ? prev.renkler.filter(r => r !== renk)
        : [...prev.renkler, renk]
    }));
  };

  const handleBedenToggle = (beden) => {
    setFormData(prev => ({
      ...prev,
      bedenler: prev.bedenler.includes(beden)
        ? prev.bedenler.filter(b => b !== beden)
        : [...prev.bedenler, beden]
    }));
  };

  const generateBarkodlar = () => {
    const barkodlar = [];
    formData.renkler.forEach(renk => {
      formData.bedenler.forEach(beden => {
        barkodlar.push({
          renk,
          beden,
          barkod: `${formData.urunKodu}${renk}${beden}`.replace(/\s/g, "")
        });
      });
    });
    setFormData(prev => ({ ...prev, barkodlar }));
  };

  useEffect(() => {
    if (currentStep === 5) {
      generateBarkodlar();
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.urunKodu || !formData.urunAdi || !formData.hierarsi) {
        toast.error("Lütfen tüm zorunlu alanları doldurun");
        return;
      }
    }
    if (currentStep === 2 && formData.renkler.length === 0) {
      toast.error("En az bir renk seçin");
      return;
    }
    if (currentStep === 3 && formData.bedenler.length === 0) {
      toast.error("En az bir beden seçin");
      return;
    }
    setCurrentStep(prev => Math.min(6, prev + 1));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/nebim/urun-ac?token=${token}`, formData);
      if (response.data.success) {
        toast.success("Ürün başarıyla oluşturuldu");
        // Reset form
        setFormData({
          urunKodu: "",
          urunAdi: "",
          kdvOrani: "8",
          hierarsi: "",
          renkler: [],
          bedenler: [],
          ozellikler: {},
          barkodlar: [],
          fiyatlar: {
            satinAlma: "",
            toptanSatis: "",
            ilkSatis: "",
            perakendeSatis: ""
          }
        });
        setCurrentStep(1);
      } else {
        toast.error(response.data.error || "Ürün oluşturulamadı");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ürün oluşturma hatası");
    } finally {
      setLoading(false);
    }
  };

  const filteredHierarsiler = hierarsiler.filter(h => 
    h.toLowerCase().includes(hierarsiArama.toLowerCase())
  );

  const filteredRenkler = renkler.filter(r => 
    r.ad.toLowerCase().includes(renkArama.toLowerCase())
  );

  return (
    <div className="space-y-6 fade-in" data-testid="urun-ac-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Yeni Ürün Ekle</h1>
          <p className="text-slate-500 text-sm">Adım {currentStep} / 6</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, idx) => (
          <div key={step.id} className="flex items-center">
            <div className={`flex flex-col items-center ${idx > 0 ? "ml-4" : ""}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                currentStep > step.id 
                  ? "bg-emerald-500 text-white" 
                  : currentStep === step.id 
                    ? "bg-emerald-500 text-white" 
                    : "bg-slate-200 text-slate-500"
              }`}>
                {currentStep > step.id ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs mt-1 ${currentStep >= step.id ? "text-slate-900" : "text-slate-400"}`}>
                {step.title}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`w-16 h-0.5 ml-4 ${currentStep > step.id ? "bg-emerald-500" : "bg-slate-200"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Content */}
      <Card>
        <CardContent className="pt-6">
          {/* Step 1: Ürün Bilgileri */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Ürün Bilgileri</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ürün Kodu *</Label>
                  <Input
                    value={formData.urunKodu}
                    onChange={(e) => setFormData({...formData, urunKodu: e.target.value})}
                    placeholder="KOD110"
                    data-testid="input-urun-kodu"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ürün Adı *</Label>
                  <Input
                    value={formData.urunAdi}
                    onChange={(e) => setFormData({...formData, urunAdi: e.target.value})}
                    placeholder="Ürün Adı"
                    data-testid="input-urun-adi"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>KDV (%) *</Label>
                <Select value={formData.kdvOrani} onValueChange={(v) => setFormData({...formData, kdvOrani: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">%0</SelectItem>
                    <SelectItem value="1">%1</SelectItem>
                    <SelectItem value="8">%8</SelectItem>
                    <SelectItem value="10">%10</SelectItem>
                    <SelectItem value="18">%18</SelectItem>
                    <SelectItem value="20">%20</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Hierarşi (Aramalı) *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                  <Input
                    value={hierarsiArama}
                    onChange={(e) => setHierarsiArama(e.target.value)}
                    placeholder="Kategori ara..."
                    className="pl-10"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border rounded-lg mt-2">
                  {filteredHierarsiler.map((h, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 cursor-pointer hover:bg-slate-50 ${formData.hierarsi === h ? "bg-emerald-50 text-emerald-700" : ""}`}
                      onClick={() => setFormData({...formData, hierarsi: h})}
                    >
                      {h}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Renk Seçimi */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Renk Seçimi (Çoklu Seçim)</h2>
                <span className="text-sm text-slate-500">Seçili: {formData.renkler.length} renk</span>
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                <Input
                  value={renkArama}
                  onChange={(e) => setRenkArama(e.target.value)}
                  placeholder="Renk ara..."
                  className="pl-10"
                />
              </div>

              <div className="grid grid-cols-3 md:grid-cols-5 gap-3 max-h-96 overflow-y-auto">
                {filteredRenkler.map((renk, idx) => (
                  <Button
                    key={idx}
                    variant={formData.renkler.includes(renk.kod) ? "default" : "outline"}
                    className={`h-auto py-3 ${formData.renkler.includes(renk.kod) ? "bg-emerald-500 hover:bg-emerald-600" : ""}`}
                    onClick={() => handleRenkToggle(renk.kod)}
                  >
                    {renk.ad}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Beden Seçimi */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Beden Seçimi (Çoklu Seçim)</h2>
                <span className="text-sm text-slate-500">Seçili: {formData.bedenler.length} beden</span>
              </div>

              <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                {bedenler.map((beden, idx) => (
                  <Button
                    key={idx}
                    variant={formData.bedenler.includes(beden) ? "default" : "outline"}
                    className={`h-auto py-3 ${formData.bedenler.includes(beden) ? "bg-emerald-500 hover:bg-emerald-600" : ""}`}
                    onClick={() => handleBedenToggle(beden)}
                  >
                    {beden}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Özellikler */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Özellikler</h2>
              <p className="text-slate-500">Ürüne özel ek özellikler tanımlayabilirsiniz.</p>
              
              <div className="text-center py-8 text-slate-400">
                Bu adım opsiyoneldir. Devam etmek için İleri butonuna tıklayın.
              </div>
            </div>
          )}

          {/* Step 5: Barkod */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Barkod Oluşturma</h2>
              <p className="text-slate-500">Toplam {formData.barkodlar.length} varyant oluşturulacak</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {formData.barkodlar.map((item, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{item.renk} - {item.beden}</span>
                      <Barcode className="w-5 h-5 text-slate-400" />
                    </div>
                    <Input
                      value={item.barkod}
                      onChange={(e) => {
                        const updated = [...formData.barkodlar];
                        updated[idx].barkod = e.target.value;
                        setFormData({...formData, barkodlar: updated});
                      }}
                      placeholder="Barkod"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 6: Fiyatlandırma */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">Fiyatlandırma</h2>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Satın Alma Fiyatı *</Label>
                  <div className="flex gap-2">
                    <Select defaultValue="TRY">
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRY">TRY</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={formData.fiyatlar.satinAlma}
                      onChange={(e) => setFormData({...formData, fiyatlar: {...formData.fiyatlar, satinAlma: e.target.value}})}
                      placeholder="Fiyat"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Toptan Satış Fiyatı *</Label>
                  <div className="flex gap-2">
                    <Select defaultValue="TRY">
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRY">TRY</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={formData.fiyatlar.toptanSatis}
                      onChange={(e) => setFormData({...formData, fiyatlar: {...formData.fiyatlar, toptanSatis: e.target.value}})}
                      placeholder="Fiyat"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>İlk Satış Fiyatı *</Label>
                  <div className="flex gap-2">
                    <Select defaultValue="TRY">
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRY">TRY</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={formData.fiyatlar.ilkSatis}
                      onChange={(e) => setFormData({...formData, fiyatlar: {...formData.fiyatlar, ilkSatis: e.target.value}})}
                      placeholder="Fiyat"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Perakende Satış Fiyatı *</Label>
                  <div className="flex gap-2">
                    <Select defaultValue="TRY">
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRY">TRY</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={formData.fiyatlar.perakendeSatis}
                      onChange={(e) => setFormData({...formData, fiyatlar: {...formData.fiyatlar, perakendeSatis: e.target.value}})}
                      placeholder="Fiyat"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button 
              variant="outline" 
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Geri
            </Button>
            
            {currentStep < 6 ? (
              <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700">
                İleri
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSave} 
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Check className="w-4 h-4 mr-2" />
                {loading ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
